import { computed, Injectable, signal } from '@angular/core'

import {
  Order,
  OrderAdditionalPackage,
  OrderDetailData,
  OrderPackage,
} from '../models/order.entity'
import { OrdersRepository } from '../orders.repository'
import { HttpErrorResponse } from '@angular/common/http'
import { Printers } from '../../print-node'
import { OrdersService } from '../orders.service'
import { Carrier } from '../../carriers'
import { Warehouse } from '../../warehouses'
import { OrderField } from '../models/order-search.model'
import { ShippingAddress } from '../../../models/address.model'
import { ORDER_ACTIONS } from '../consts/order-statuses.const'
import { getOrderPickedQty } from '../libs/order-picking.lib'

interface OrderPackingDetailState {
  order?: Order
  carrier?: Carrier
  warehouse?: Warehouse
  error?: HttpErrorResponse
  fields: OrderField[]
  printers?: Printers
  isLoading: boolean
  isModified: boolean
}

const ORDER_DETAIL_INITIAL_STATE: OrderPackingDetailState = {
  isLoading: false,
  isModified: false,
  fields: ['carrierId', 'assignedWarehouseId'],
}

@Injectable()
export class OrderPackingDetailStore {
  constructor(
    private ordersRepository: OrdersRepository,
    private ordersService: OrdersService,
  ) {}

  private state = signal<OrderPackingDetailState>(ORDER_DETAIL_INITIAL_STATE)

  // Selectors
  readonly order = computed(() => this.state().order)
  readonly carrier = computed(() => this.state().carrier)
  readonly warehouse = computed(() => this.state().warehouse)
  readonly printers = computed(() => this.state().printers)
  readonly permissions = computed(() =>
    this.order()?.status ? ORDER_ACTIONS[this.order()!.status].permissions : [],
  )
  readonly pickedQty = computed(() =>
    this.order() ? getOrderPickedQty(this.order()!) : 0,
  )
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  /*   readonly selectOrderStatus$ = this.selectOrder$.pipe(
    map((order) => order?.status),
  )
  readonly selectOrderShippingAddress$ = this.selectOrder$.pipe(
    map((order) => order?.header.shippingAddress),
  )

  readonly selectOrderPickedQty$ = this.selectOrder$.pipe(
    map((order) => (order ? getOrderPickedQty(order) : 0)),
  )

  readonly selectOrderPackages$ = this.selectOrder$.pipe(
    map((order) => order?.packages),
  )

  readonly selectOrderAdditionalPackages$ = this.selectOrder$.pipe(
    map((order) => order?.additionalPackages),
  )

  readonly selectPermissions$ = this.selectOrderStatus$.pipe(
    map((status) => (status ? ORDER_ACTIONS[status].permissions : [])),
  )

  readonly selectExtData$ = this.select((state) => state.extData)

  readonly selectWarehouse$ = this.selectExtData$.pipe(
    map((extData) => extData?.warehouse),
  )

  readonly selectCarrier$ = this.selectExtData$.pipe(
    map((extData) => extData?.carrier),
  )

  readonly selectNotification$ = this.selectState$.pipe(
    map((state) => state.notification),
  )
 */
  // Effects

  async load(orderId?: string): Promise<void> {
    if (!orderId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      const fields = this.state().fields
      this.ordersRepository.loadOrder$(orderId, fields).subscribe({
        next: async (data) => {
          if (!data.order) {
            this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
            reject()
            return
          }
          resolve()
          this.setData(
            data.order,
            this.state().order?._id
              ? this.state().order?._id !== data.order._id
              : false,
            data.extData,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(order: Order, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      const fields = this.state().fields
      this.ordersRepository.saveOrder$(order, fields).subscribe({
        next: (data) => {
          if (!data.order) {
            resolve(false)
            this.ordersRepository.notify({ code: 'ORDER_NOT_SAVED' })
            return
          }
          resolve(true)
          this.setData(data.order, false, data.extData)
          if (notify === true) {
            this.ordersRepository.notify({ code: 'ORDER_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async printLabel(): Promise<boolean> {
    this.unsetError()

    const order = this.order()
    const printers = this.printers()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      this.setIsLoading(false)
      return false
    }
    if (!printers) {
      this.ordersRepository.notify({ code: 'PRINTERS_NOT_FOUND' })
      this.setIsLoading(false)
      return false
    }

    const isModified = this.isModified()
    if (isModified) {
      const saved = await this.save(order)
      if (!saved) {
        this.setIsLoading(false)
        return false
      }
    }

    return new Promise((resolve) => {
      this.ordersService.printLabel$(order._id, printers).subscribe({
        next: () => {
          this.ordersRepository.notify({ code: 'ORDER_PRINTED' })
          this.setIsLoading(false)
          resolve(true)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async cancelShipment(): Promise<boolean> {
    this.unsetError()
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }

    return new Promise((resolve) => {
      this.ordersService.cancelShipment$(order._id).subscribe({
        next: () => {
          resolve(true)
          this.ordersRepository.notify({ code: 'SHIPMENT_CANCELED' })
          this.load(order._id)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async requestShipment(): Promise<boolean> {
    this.unsetError()
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return false
    }

    return new Promise((resolve) => {
      this.ordersService.requestShipment$(order._id).subscribe({
        next: () => {
          resolve(true)
          this.ordersRepository.notify({ code: 'SHIPMENT_REQUESTED' })
          this.load(order._id)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  setShippingAddress(shippingAddress: ShippingAddress) {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return
    }

    order.header.shippingAddress = shippingAddress
    this.setOrder(order)
  }

  setPackages(packages: OrderPackage[] | undefined) {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return
    }

    order.packages = packages
    this.setOrder(order)
  }

  setAdditionalPackages(
    additionalPackages: OrderAdditionalPackage[] | undefined,
  ) {
    const order = this.order()
    if (!order) {
      this.ordersRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return
    }

    order.additionalPackages = additionalPackages
    this.setOrder(order)
  }

  setData(order: Order, isModified?: boolean, extData?: OrderDetailData) {
    this.state.set({
      ...this.state(),
      order: order,
      isModified: isModified ?? this.state().isModified,
      carrier: extData?.carrier,
      warehouse: extData?.warehouse,
      isLoading: false,
      error: undefined,
    })
  }

  setOrder(order: Order) {
    this.state.set({
      ...this.state(),
      order: order,
      isModified: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  setPrinters(printers?: Printers) {
    this.state.set({ ...this.state(), isLoading: false, printers })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  unsetOrder() {
    this.state.set({ ...this.state(), order: undefined, error: undefined })
  }
}
